import FormInputComponent from "@via/components/inputs/form-input-component";
import { useEditOrganization } from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { editOrganizationMarginOptions } from "@via/utils/form-validators";
import { GetUserOrganizationDetailsForAdminResponseBodyItem } from "common";
import { forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";

export interface PanelSubmitHandle {
  /** Resolves false to keep the modal open, matching Modal's onConfirm contract. */
  submit: () => Promise<boolean>;
}

/**
 * The General tab: identity, and the margin when there is somewhere to write it.
 *
 * Margin is rendered only when the organization has a billing record. organization_billing rows
 * are written only by Stripe paths, so on a self hosted install the table is empty and the UPDATE
 * behind this field matches nothing. The backend refuses that write now, and this hides the field
 * rather than letting an administrator discover the refusal by typing into it.
 */
const GeneralPanel = forwardRef<
  PanelSubmitHandle,
  { organization: GetUserOrganizationDetailsForAdminResponseBodyItem }
>(({ organization }, ref) => {
  const { mutate: editOrganization } = useEditOrganization();

  const { control, handleSubmit } = useForm({
    mode: "onChange",
    defaultValues: { margin: organization.margin || 0 },
  });

  /**
   * react-hook-form's handleSubmit does NOT throw on a failed validation, it simply skips the
   * callback and resolves. Tracking whether the callback ran is the only way to tell Modal to stay
   * open: closing on an invalid submit would destroy both the input and the errors just rendered.
   */
  const submit = async (): Promise<boolean> => {
    if (!organization.hasBillingRecord) {
      return true;
    }
    let submitted = false;
    await handleSubmit(payload => {
      editOrganization({
        margin: parseFloat((+payload.margin).toFixed(2)),
        organizationId: organization.id,
      });
      submitted = true;
    })();
    return submitted;
  };

  useImperativeHandle(ref, () => ({ submit }));

  return (
    <form onSubmit={e => e.preventDefault()}>
      <dl className="grid grid-cols-2 gap-4 pb-4 text-sm">
        <div>
          <dt className="font-medium text-gray-500">Organization id</dt>
          <dd data-testid="organization-id" className="break-all">
            {organization.id}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-gray-500">Members</dt>
          <dd data-testid="organization-member-count">
            {organization.users.length}
          </dd>
        </div>
      </dl>

      {organization.hasBillingRecord ? (
        <div className="space-y-6 pb-5 pt-2">
          <FormInputComponent
            control={control}
            name="margin"
            label="Margin (%)"
            placeholder="Enter Margin Percentage"
            type="number"
            step="0.01"
            min="0"
            validations={editOrganizationMarginOptions.margin}
          />
        </div>
      ) : (
        <p
          data-testid="margin-unavailable"
          className="pb-5 pt-2 text-sm text-gray-500">
          Margin applies once billing is configured for this organization. There
          is no billing record yet, so there is nothing to set.
        </p>
      )}
    </form>
  );
});

GeneralPanel.displayName = "GeneralPanel";

export default GeneralPanel;
